import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FlightManagementTable from '../../../airliner/components/FlightManagementTable/FlightManagementTable';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useFlightManagement } from '../../../airliner/hooks';
import { fetchFlights } from '../../../store/slices/flightManagementSlice';
import { useAppDispatch } from '../../../store/hooks';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import adminService from '../../../services/adminService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to format dates in user's local timezone
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const AdminFlightManagement = () => {
  const dispatch = useAppDispatch();
  const { airlinerId } = useParams();
  const id = airlinerId;
  const navigate = useNavigate();
  const { flights, loading, error, loadFlights, removeFlight, cancelFlightById, restoreDeletedFlight, permanentlyDeleteFlight, pagination } = useFlightManagement();
  const { perms } = useAdminPermissions();
  const canEditFlights = perms.flights?.edit === true;
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [airliners, setAirliners] = useState([]);
  const [selectedAirliner, setSelectedAirliner] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Refs for click outside detection
  const exportDropdownRef = useRef(null);
  const columnDropdownRef = useRef(null);

  // Separate state for applied filters (what's actually sent to API)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    airliner: '',
    status: ''
  });
  const [visibleColumns, setVisibleColumns] = useState({
    regNo: true,
    flightNo: true,
    airliner: true,
    depAirport: true,
    etd: true,
    arrAirport: true,
    eta: true,
    belPrice: true,
    blPrice: true,
    seatPrice: true,
    seatTotal: true,
    seatAvailable: true,
    flightTime: false,
    status: true,
    action: true
  });
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'warning',
    title: '',
    message: '',
    confirmText: '',
    onConfirm: null
  });
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Fetch airliners list
  useEffect(() => {
    const fetchAirliners = async () => {
      try {
        const response = await adminService.getAirlinersList();
        if (response.success) {
          setAirliners(response.data || []);
        }
      } catch (error) {
        console.error('❌ Failed to fetch airliners:', error);
      }
    };
    fetchAirliners();
  }, []);

  // Click outside detection for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const params = {
      sortBy: sortBy,
      sortOrder: sortOrder,
      page: currentPage,
      limit: recordsPerPage,
      search: appliedFilters.search
    };

    if (appliedFilters.airliner) {
      params.airliner_id = appliedFilters.airliner;
    }

    // Send status to backend for server-side filtering
    if (appliedFilters.status) {
      params.status = appliedFilters.status;
    }

    if (id) {
      // Airliner specific
      dispatch(fetchFlights({ airliner_id: id, ...params }));
    } else {
      // Admin global
      dispatch(fetchFlights(params)).then((res) => {
        console.log("Admin flight-management response:", res);
      });
    }
  }, [dispatch, id, sortBy, sortOrder, currentPage, recordsPerPage, appliedFilters]);

  // Data is now handled entirely on the server with all filters
  const serverTotalRecords = pagination?.totalItems || 0;
  const serverTotalPages = pagination?.totalPages || 1;

  // Use server data directly - no client-side filtering needed
  const currentData = flights || [];

  // Use server pagination values
  const totalRecords = serverTotalRecords;
  const totalPages = serverTotalPages;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExport = (format) => {
    // Get visible column keys (exclude 'action')
    const visibleCols = Object.keys(visibleColumns).filter(key => visibleColumns[key] && key !== 'action');

    const labels = {
      regNo: 'Reg No.',
      flightNo: 'Flight No.',
      airliner: 'Airliner',
      depAirport: 'Dep. Airport',
      etd: 'ETD',
      arrAirport: 'Arr. Airport',
      eta: 'ETA',
      belPrice: 'BEL Price',
      blPrice: 'EL Price',
      seatPrice: 'Seat Price',
      seatTotal: 'Total Seat',
      seatAvailable: 'Available Seat',
      flightTime: 'Flight Time',
      status: 'Status'
    };

    const dataToExport = flights.map((flight) => {
      const row = { '#': flight.seq_id };
      visibleCols.forEach(key => {
        let val = '';
        switch (key) {
          case 'regNo':
            val = flight.registration_number || flight.aircraft?.registration_number || 'N/A';
            break;
          case 'flightNo':
            val = flight.flight_number || 'N/A';
            break;
          case 'airliner':
            // First priority: Get from nested aircraft.user object (from API response)
            if (flight.aircraft?.user) {
              const fullName = [flight.aircraft.user.first_name, flight.aircraft.user.last_name].filter(Boolean).join(' ').trim();
              if (fullName) {
                val = fullName;
                break;
              }
              val = flight.aircraft.user.company_name || flight.aircraft.user.email || '-';
              break;
            }
            // Second priority: Lookup airliner by airliner_id or user_id from airliners list
            const airlinerId = flight.airliner_id || flight.user_id || flight.aircraft?.user_id;
            if (airlinerId && airliners && airliners.length > 0) {
              const airliner = airliners.find(a => Number(a.id) === Number(airlinerId));
              if (airliner) {
                const fullName = [airliner.first_name, airliner.last_name].filter(Boolean).join(' ').trim();
                val = fullName || airliner.company_name || airliner.email || '-';
                break;
              }
            }
            // Third priority: Check for flat airliner_name field
            if (flight.airliner_name) {
              val = flight.airliner_name;
            } else if (flight.airliner) {
              // Fourth priority: Build airliner name from flight.airliner relationship
              const fullName = [flight.airliner.first_name, flight.airliner.last_name].filter(Boolean).join(' ').trim();
              val = fullName || flight.airliner.company_name || flight.airliner.email || '-';
            } else {
              val = '-';
            }
            break;
          case 'depAirport':
            val = flight.dep_airport || flight.departureAirport?.iata_code || 'N/A';
            break;
          case 'etd':
            val = formatDateTime(flight.departure_date_time);
            break;
          case 'arrAirport':
            val = flight.arr_airport || flight.arrivalAirport?.iata_code || 'N/A';
            break;
          case 'eta':
            val = formatDateTime(flight.arrival_date_time);
            break;
          case 'belPrice':
            val = `$${flight.block_empty_price || 0}`;
            break;
          case 'blPrice':
            val = `$${flight.block_price || 0}`;
            break;
          case 'seatPrice':
            val = `$${flight.seat_price || 0}`;
            break;
          case 'seatTotal':
            val = flight.seat_total || 0;
            break;
          case 'seatAvailable':
            val = flight.seat_available || 0;
            break;
          case 'flightTime':
            val = flight.flight_time || 'N/A';
            break;
          case 'status':
            const statusValue = (flight.status || '').toLowerCase();
            const notOperateFlight = Number(flight.not_operate_flight);
            const bookingCount = Number(flight.booking_count);
            if (statusValue === 'published') {
              if (notOperateFlight === 1) {
                val = bookingCount === 0 ? 'DNO' : 'Completed';
              } else {
                val = 'Active';
              }
            } else if (statusValue === 'canceled' || statusValue === 'cancelled') {
              val = 'Cancelled';
            } else if (statusValue === 'draft' || statusValue === 'saved') {
              val = 'Saved';
            } else if (flight.deleted_at) {
              val = 'Deleted';
            } else {
              val = flight.status || 'N/A';
            }
            break;
          default:
            val = '';
        }
        row[labels[key] || key] = val;
      });
      return row;
    });

    if (format === 'excel') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Flights");
      const fileName = `flight_data_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } else if (format === 'pdf') {
      const doc = new jsPDF('landscape');
      const tableColumn = ["#", ...visibleCols.map(key => labels[key] || key)];
      const tableRows = dataToExport.map(row => Object.values(row));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [255, 235, 0], textColor: [0, 0, 0] } // Yellow background, black text to match theme
      });
      doc.text("Flight Management List", 14, 15);
      const fileName = `flight_data_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    }

    setShowExportDropdown(false);
  };

  const handleRecordsPerPageChange = (e) => {
    // Removed - no longer needed
  };

  const handleFilterApply = () => {
    // Apply filters and reset to page 1
    setAppliedFilters({
      search: searchTerm,
      airliner: selectedAirliner,
      status: selectedStatus
    });
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    // Reset all filter inputs
    setSearchTerm('');
    setSelectedAirliner('');
    setSelectedStatus('');

    // Reset applied filters
    setAppliedFilters({
      search: '',
      airliner: '',
      status: ''
    });

    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    console.log('📄 Page changed to:', pageNum);
    setCurrentPage(pageNum);
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  const handleViewFlight = (flightId) => {
    if (id) {
      navigate(`/admin/airliners/${id}/flights/${flightId}`);
    } else {
      navigate(`/admin/admin-flights/${flightId}`);
    }
  };

  const handleEditFlight = (flightId) => {
    if (id) {
      navigate(`/admin/airliners/${id}/flights/${flightId}/edit`);
    } else {
      navigate(`/admin/admin-flights/${flightId}/edit`);
    }
  };

  const handleDeleteFlight = (flightId) => {
    setModalConfig({
      isOpen: true,
      type: 'permanentDelete',
      title: 'Delete Flight',
      message: 'Are you sure you want to delete this flight? This action cannot be undone.',
      confirmText: 'YES, DELETE',
      onConfirm: async () => {
        try {
          await removeFlight(flightId);
          toast.success('Flight deleted successfully!');
          loadFlights({ airliner_id: id });
        } catch (e) {
          toast.error('Failed to delete flight');
        }
      }
    });
  };

  const handleCancelFlight = (flightId) => {
    setModalConfig({
      isOpen: true,
      type: 'cancel',
      title: 'Cancel Flight',
      message: 'Are you sure you want to cancel this flight?',
      confirmText: 'YES, CANCEL',
      onConfirm: async () => {
        try {
          await cancelFlightById(flightId);
          toast.success('Flight cancelled successfully!');
          loadFlights({ airliner_id: id });
        } catch (e) {
          const message =
            typeof e === 'string' ? e : e?.message || 'Failed to cancel flight';
          toast.error(message);
        }
      }
    });
  };

  const handleRestoreFlight = (flightId) => {
    setModalConfig({
      isOpen: true,
      type: 'restore',
      title: 'Restore Flight',
      message: 'Are you sure you want to restore this flight?',
      confirmText: 'YES, RESTORE',
      onConfirm: async () => {
        try {
          await restoreDeletedFlight(flightId);
          toast.success('Flight restored successfully!');
          loadFlights({ airliner_id: id });
        } catch (e) {
          toast.error('Failed to restore flight');
        }
      }
    });
  };

  const handlePermanentDeleteFlight = (flightId) => {
    setModalConfig({
      isOpen: true,
      type: 'permanentDelete',
      title: 'Delete Flight',
      message: 'Are you sure you want to permanently delete this flight? This action cannot be undone.',
      confirmText: 'YES, DELETE',
      onConfirm: async () => {
        try {
          await permanentlyDeleteFlight(flightId);
          toast.success('Flight permanently deleted successfully!');
          loadFlights({ airliner_id: id });
        } catch (e) {
          toast.error('Failed to permanently delete flight');
        }
      }
    });
  };

  return (
    <div className="p-6 max-sm:p-3 w-full h-full flex flex-col bg-gray-50">
      {/* Header Section - Outside white container */}
      <div className="flex items-center justify-between mb-4 max-sm:flex-col max-sm:items-start max-sm:gap-3">
        <h1 className="text-xl font-semibold text-gray-800">Flight Management</h1>
        <div className="flex gap-2 max-sm:flex-wrap">
          <div className="relative" ref={exportDropdownRef}>
            <button
              className="text-black h-9 px-4 text-sm font-medium bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-sm hover:shadow-md transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300] flex items-center gap-2"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export
            </button>
            {showExportDropdown && (
              <div className="absolute top-full right-0 bg-white border border-gray-200 rounded shadow-lg z-10 min-w-[140px] mt-1 overflow-hidden">
                <div onClick={() => handleExport('excel')} className="py-2.5 px-4 cursor-pointer text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <FontAwesomeIcon icon={faFileExcel} className="text-green-600" /> Excel
                </div>
                <div onClick={() => handleExport('pdf')} className="py-2.5 px-4 cursor-pointer text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  <FontAwesomeIcon icon={faFilePdf} className="text-red-600" /> PDF
                </div>
              </div>
            )}
          </div>
          <div className="relative" ref={columnDropdownRef}>
            <button
              className="text-black h-9 px-4 text-sm font-medium bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-sm hover:shadow-md transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300] flex items-center gap-2"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
              Hide/Show Column ▼
            </button>
            {showColumnDropdown && (
              <div className="absolute top-full right-0 bg-white border border-gray-200 rounded shadow-lg py-2 z-10 min-w-[220px] mt-1 max-h-[400px] overflow-y-auto">
                {Object.keys(visibleColumns).map(key => (
                  <label
                    key={key}
                    onClick={() => toggleColumnVisibility(key)}
                    className="flex items-center py-2 px-4 cursor-pointer text-sm text-gray-700 hover:bg-gray-50 gap-3"
                  >
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${visibleColumns[key] ? 'bg-[#ffd200]' : 'bg-white border-2 border-gray-300'}`}>
                      {visibleColumns[key] && <span className="text-black text-base font-bold">✓</span>}
                    </span>
                    <span className="flex-1 text-gray-700">
                      {key === 'regNo' && 'Reg No.'}
                      {key === 'flightNo' && 'Flight No.'}
                      {key === 'airliner' && 'Airliner'}
                      {key === 'depAirport' && 'Dep. Airport'}
                      {key === 'etd' && 'ETD'}
                      {key === 'arrAirport' && 'Arr. Airport'}
                      {key === 'eta' && 'ETA'}
                      {key === 'belPrice' && 'BEL Price'}
                      {key === 'blPrice' && 'BL Price'}
                      {key === 'seatPrice' && 'Seat Price'}
                      {key === 'seatTotal' && 'Total Seat'}
                      {key === 'seatAvailable' && 'Available Seat'}
                      {key === 'flightTime' && 'Flight Time'}
                      {key === 'status' && 'Status'}
                      {key === 'action' && 'Action'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Section - In white container with divider */}
      <div className="bg-white mb-4">
        <div className="p-4">
          <h2 className="text-sm font-bold text-gray-700 mb-3">Filters</h2>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 max-sm:flex-col max-sm:items-start max-sm:w-full">
              <label className="text-sm text-gray-600 font-semibold">Search:</label>
              <input
                type="text"
                placeholder="Flight # or Registration"
                value={searchTerm}
                onChange={handleSearch}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent w-48 max-sm:w-full"
                style={{ textTransform: 'none' }}
              />
            </div>
            <div className="flex items-center gap-2 max-sm:flex-col max-sm:items-start max-sm:w-full">
              <label className="text-sm text-gray-600 font-semibold">Airliner:</label>
              <select
                value={selectedAirliner}
                onChange={(e) => setSelectedAirliner(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                style={{ textTransform: 'none' }}
              >
                <option value="">All Airliners</option>
                {airliners.length > 0 ? (
                  airliners.map((airliner) => {
                    // Build display name: prioritize first_name + last_name, then company_name, then email
                    const fullName = [airliner.first_name, airliner.last_name].filter(Boolean).join(' ').trim();
                    const displayName = fullName || airliner.company_name || airliner.email || `Airliner ${airliner.id}`;

                    return (
                      <option key={airliner.id} value={airliner.id}>
                        {displayName}
                      </option>
                    );
                  })
                ) : (
                  <option disabled>Loading airliners...</option>
                )}
              </select>
            </div>
            <div className="flex items-center gap-2 max-sm:flex-col max-sm:items-start max-sm:w-full">
              <label className="text-sm text-gray-600 font-semibold">Status:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent min-w-[200px] max-sm:w-full max-sm:min-w-0"
                style={{ textTransform: 'none' }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="saved">Saved</option>
                <option value="dno">DNO</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
            <button
              onClick={handleFilterApply}
              className="px-4 py-1.5 text-sm font-medium text-black bg-gradient-to-br from-[#ffeb00] to-[#ffd200] rounded hover:bg-[#2d6a8f] transition-colors"
            >
              Filter
            </button>
            <button
              onClick={handleFilterReset}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

      </div>

      {/* Table Section */}
      <div className="bg-white flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <FlightManagementTable
            flights={currentData}
            loading={loading}
            error={error}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            currentPage={currentPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onPageChange={handlePageChange}
            visibleColumns={visibleColumns}
            airliners={airliners}
            onViewFlight={handleViewFlight}
            onEditFlight={canEditFlights ? handleEditFlight : null}
            onDeleteFlight={canEditFlights ? handleDeleteFlight : null}
            onCancelFlight={canEditFlights ? handleCancelFlight : null}
            onRestoreFlight={handleRestoreFlight}
            onPermanentDeleteFlight={handlePermanentDeleteFlight}
            sortConfig={{ key: sortBy, direction: sortOrder.toLowerCase() }}
            onSortChange={handleSort}
            totalRecords={totalRecords}
            totalPages={totalPages}
            hideTopControls={true}
            hidePagination={false}
          />
        </div>
      </div>
      <UniversalConfirmModal
        isOpen={modalConfig.isOpen}
        onConfirm={modalConfig.onConfirm}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText="CANCEL"
        theme="modern"
      />
    </div>
  );
};

export default AdminFlightManagement;